import React, { useState, useMemo } from 'react';
import { TransactionComp, AccessTier } from '../../types';
import {
  TrendingUp,
  Search,
  ArrowUpDown,
  Sliders,
  DollarSign,
  Zap,
  MapPin,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  Building,
} from 'lucide-react';
import { getCountyRecorderUrl, getIsoQueuePortalUrl } from '../../lib/sourceLinks';

interface ArbitrageScreenerProps {
  comps: TransactionComp[];
  tier: AccessTier;
  onUnderwriteArbitrage: (comp: TransactionComp, spread: number, multiple: number) => void;
  onSelectComp: (comp: TransactionComp) => void;
  onOpenAuthModal: () => void;
}

interface ArbitrageItem {
  comp: TransactionComp;
  rawDirtBasisUsd: number;
  substationBasisUsd: number;
  totalCostBasisUsd: number;
  impliedMarketValueUsd: number;
  grossSpreadUsd: number;
  arbitrageMultiple: number;
  timeToPowerYears: number;
  arbitrageScore: number;
}

export const ArbitrageScreener: React.FC<ArbitrageScreenerProps> = ({
  comps,
  tier,
  onUnderwriteArbitrage,
  onSelectComp,
  onOpenAuthModal,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIso, setSelectedIso] = useState('ALL');
  const [minMultiple, setMinMultiple] = useState<number>(2.0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Compute arbitrage spreads for each transaction
  const computedArbitrage = useMemo<ArbitrageItem[]>(() => {
    return comps.map((c) => {
      const acres = c.headline_acreage || 150;
      const mw = c.headline_capacity_mw || 250;

      // 1. Raw dirt basis (agricultural benchmark: ~$25k/acre)
      const rawDirtBasis = acres * 25_000;

      // 2. Substation & HV interconnection basis (~$22M baseline for 345/500kV)
      const substationBasis = mw >= 500 ? 38_000_000 : mw >= 250 ? 24_000_000 : 16_000_000;

      const totalBasis = rawDirtBasis + substationBasis;

      // 3. Shovel-ready market value
      const impliedVal = c.total_consideration_usd
        ? c.total_consideration_usd
        : c.implied_price_per_mw_usd
        ? c.implied_price_per_mw_usd * mw
        : mw * 286_000;

      const grossSpread = Math.max(0, impliedVal - totalBasis);
      const multiple = totalBasis > 0 ? impliedVal / totalBasis : 1.0;
      const timeToPower = mw >= 500 ? 3.5 : 2.5;

      // Arbitrage Opportunity Score (1-100)
      const score = Math.min(
        99,
        Math.round((multiple * 12) + (grossSpread > 50_000_000 ? 25 : 10) + (mw >= 300 ? 15 : 5))
      );

      return {
        comp: c,
        rawDirtBasisUsd: rawDirtBasis,
        substationBasisUsd: substationBasis,
        totalCostBasisUsd: totalBasis,
        impliedMarketValueUsd: impliedVal,
        grossSpreadUsd: grossSpread,
        arbitrageMultiple: Number(multiple.toFixed(2)),
        timeToPowerYears: timeToPower,
        arbitrageScore: score,
      };
    });
  }, [comps]);

  // Filter
  const filtered = useMemo(() => {
    return computedArbitrage.filter((item) => {
      if (item.arbitrageMultiple < minMultiple) return false;
      if (selectedIso !== 'ALL' && item.comp.iso_rto?.toUpperCase() !== selectedIso) return false;
      if (search) {
        const s = search.toLowerCase().trim();
        return (
          (item.comp.deal_name && item.comp.deal_name.toLowerCase().includes(s)) ||
          (item.comp.canonical_project_id && item.comp.canonical_project_id.toLowerCase().includes(s)) ||
          (item.comp.canonical_name && item.comp.canonical_name.toLowerCase().includes(s)) ||
          (item.comp.county && item.comp.county.toLowerCase().includes(s)) ||
          (item.comp.state && item.comp.state.toLowerCase().includes(s)) ||
          (item.comp.buyer_organization_id && item.comp.buyer_organization_id.toLowerCase().includes(s))
        );
      }
      return true;
    }).sort((a, b) => b.grossSpreadUsd - a.grossSpreadUsd);
  }, [computedArbitrage, minMultiple, selectedIso, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Macro metrics for the header
  const totalMarketSpread = useMemo(() => {
    return filtered.reduce((acc, curr) => acc + curr.grossSpreadUsd, 0);
  }, [filtered]);

  const avgMultiple = useMemo(() => {
    if (filtered.length === 0) return 0;
    return (filtered.reduce((acc, curr) => acc + curr.arbitrageMultiple, 0) / filtered.length).toFixed(1);
  }, [filtered]);

  return (
    <div className="flex flex-col h-full bg-[#06090e] overflow-hidden select-none">
      {/* 1. Value Proposition Banner */}
      <div className="p-3 bg-[#0c121e] border-b border-[#1a253b] flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-0.5">
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-bold flex items-center">
              <Sparkles className="w-3 h-3 mr-1" />
              PROPRIETARY VALUATION SPREAD
            </span>
            <span className="text-xs text-slate-400 font-mono">
              QUEUE-TO-DEED TIME-TO-POWER ARBITRAGE INDEX
            </span>
          </div>
          <p className="text-xs text-slate-300">
            Identifies the valuation spread between <b className="text-slate-100">Agricultural Land Basis</b> + <b className="text-slate-100">Substation Capex</b> vs. <b className="text-emerald-400">Shovel-Ready Hyperscale Exit Price</b>.
          </p>
        </div>

        {/* Aggregate Stats */}
        <div className="flex items-center space-x-4 font-mono text-xs">
          <div className="bg-[#06090e] px-3 py-1.5 rounded-lg border border-[#1a253b]">
            <span className="text-slate-500 text-[10px] block">Total Screened Spread</span>
            <span className="font-bold text-emerald-400 text-sm">
              ${(totalMarketSpread / 1_000_000_000).toFixed(2)}B
            </span>
          </div>
          <div className="bg-[#06090e] px-3 py-1.5 rounded-lg border border-[#1a253b]">
            <span className="text-slate-500 text-[10px] block">Avg Value Uplift</span>
            <span className="font-bold text-amber-400 text-sm">
              {avgMultiple}x Multiple
            </span>
          </div>
        </div>
      </div>

      {/* 2. Controls & Filter Bar */}
      <div className="p-3 bg-[#0c121e]/80 border-b border-[#1a253b] flex flex-wrap items-center justify-between gap-3">
        {/* Search */}
        <div className="flex items-center space-x-2 flex-1 min-w-[280px] max-w-md">
          <div className="relative w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-500" />
            <input
              type="text"
              placeholder="Search campus, county, buyer, or project ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 outline-none hover:border-slate-700 focus:border-[#2979ff] transition"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 text-xs flex-wrap gap-y-2">
          {/* Minimum Multiple */}
          <select
            value={minMultiple}
            onChange={(e) => {
              setMinMultiple(Number(e.target.value));
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value={1.5}>Min 1.5x Value Uplift</option>
            <option value={2.0}>Min 2.0x Value Uplift (Standard)</option>
            <option value={3.0}>Min 3.0x Value Uplift (High Spread)</option>
            <option value={4.0}>Min 4.0x Value Uplift (Tier 1 Arbitrage)</option>
          </select>

          {/* ISO Filter */}
          <select
            value={selectedIso}
            onChange={(e) => {
              setSelectedIso(e.target.value);
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value="ALL">All ISO / RTO Queues</option>
            <option value="PJM">PJM Interconnection</option>
            <option value="ERCOT">ERCOT (Texas)</option>
            <option value="NYISO">NYISO (New York)</option>
            <option value="MISO">MISO (Midcontinent)</option>
            <option value="CAISO">CAISO (California)</option>
          </select>
        </div>

        <div className="text-slate-400 font-mono text-[11px]">
          Showing <b className="text-white">{filtered.length}</b> arbitrage opportunities
        </div>
      </div>

      {/* 3. Full-Width Arbitrage Table */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-[#1a253b] bg-[#0c121e] text-slate-400 font-mono text-[11px] uppercase tracking-wider">
              <th className="py-3 px-4 font-semibold">Campus &amp; Location</th>
              <th className="py-3 px-4 font-semibold text-right">Capacity (MW)</th>
              <th className="py-3 px-4 font-semibold text-right">Raw Dirt Basis</th>
              <th className="py-3 px-4 font-semibold text-right">Substation Tie-In</th>
              <th className="py-3 px-4 font-semibold text-right">Total Basis</th>
              <th className="py-3 px-4 font-semibold text-right">Shovel-Ready Value</th>
              <th className="py-3 px-4 font-semibold text-right">Gross Spread ($M)</th>
              <th className="py-3 px-4 font-semibold text-center">Value Multiple</th>
              <th className="py-3 px-4 font-semibold text-right">Underwrite</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#1a253b]/60">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-16 text-center text-slate-500 font-mono">
                  No arbitrage spreads meet your minimum criteria. Try adjusting the value multiple filter.
                </td>
              </tr>
            ) : (
              paginated.map((item) => {
                const c = item.comp;
                return (
                  <tr
                    key={c.deal_id || c.canonical_project_id}
                    onClick={() => onSelectComp(c)}
                    className="cursor-pointer transition-colors group hover:bg-[#0c121e]/80"
                  >
                    {/* 1. Campus & Location */}
                    <td className="py-3 px-4">
                      <div className="space-y-0.5">
                        <div className="flex items-center space-x-1.5">
                          <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-[#2979ff]/10 text-[#2979ff] border border-[#2979ff]/30 font-bold">
                            {c.canonical_project_id}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400 font-semibold">
                            {c.state}
                          </span>
                          {c.iso_rto && (
                            <span className="text-[10px] font-mono px-1 rounded bg-[#162035] text-slate-400">
                              {c.iso_rto}
                            </span>
                          )}
                        </div>
                        <div className="font-semibold text-white group-hover:text-[#2979ff] transition-colors leading-tight">
                          {c.canonical_name || c.deal_name}
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center space-x-1">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span>{c.county ? `${c.county} County, ` : ''}{c.state}</span>
                        </div>
                      </div>
                    </td>

                    {/* 2. Capacity */}
                    <td className="py-3 px-4 text-right font-mono font-bold text-white">
                      {c.headline_capacity_mw ? `${c.headline_capacity_mw.toLocaleString()} MW` : 'N/A'}
                    </td>

                    {/* 3. Raw Dirt Basis */}
                    <td className="py-3 px-4 text-right font-mono text-slate-300">
                      ${(item.rawDirtBasisUsd / 1_000_000).toFixed(1)}M
                      <div className="text-[10px] text-slate-500 font-mono">{c.headline_acreage || 150} ac</div>
                    </td>

                    {/* 4. Substation Tie-In */}
                    <td className="py-3 px-4 text-right font-mono text-cyan-400">
                      ${(item.substationBasisUsd / 1_000_000).toFixed(1)}M
                      <div className="text-[10px] text-slate-500 font-mono">High-Voltage</div>
                    </td>

                    {/* 5. Total Cost Basis */}
                    <td className="py-3 px-4 text-right font-mono font-bold text-slate-200">
                      ${(item.totalCostBasisUsd / 1_000_000).toFixed(1)}M
                    </td>

                    {/* 6. Shovel-Ready Value */}
                    <td className="py-3 px-4 text-right font-mono font-bold text-white">
                      ${(item.impliedMarketValueUsd / 1_000_000).toFixed(1)}M
                    </td>

                    {/* 7. Gross Arbitrage Spread */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className="text-emerald-400 font-bold text-sm">
                        +${(item.grossSpreadUsd / 1_000_000).toFixed(1)}M
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Net Value Uplift
                      </div>
                    </td>

                    {/* 8. Multiple Badge */}
                    <td className="py-3 px-4 text-center font-mono">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${
                        item.arbitrageMultiple >= 4.0
                          ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                          : item.arbitrageMultiple >= 2.5
                          ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                          : 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                      }`}>
                        {item.arbitrageMultiple}x
                      </span>
                    </td>

                    {/* 9. Action */}
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnderwriteArbitrage(c, item.grossSpreadUsd, item.arbitrageMultiple);
                        }}
                        className="px-2.5 py-1 rounded bg-[#2979ff]/15 hover:bg-[#2979ff]/25 border border-[#2979ff]/30 text-[#2979ff] hover:text-white font-mono text-[11px] font-semibold transition"
                      >
                        Model Deal &rarr;
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 4. Pagination Footer */}
      <div className="p-3 bg-[#0c121e] border-t border-[#1a253b] flex items-center justify-between text-xs text-slate-400 font-mono">
        <div className="flex items-center space-x-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded px-2 py-1 outline-none text-xs"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-slate-500">| Page {page} of {totalPages}</span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
